import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState(false);
  const [password, setPassword] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const ADMIN_PASS = 'sanwariya2024admin';

  useEffect(() => {
    if (auth) fetchInquiries();
  }, [auth]);

  const fetchInquiries = async () => {
    try {
      const res = await axios.get('/api/inquiries');
      setInquiries(res.data);
    } catch {
      // Demo data if backend not connected
      setInquiries([
        { _id: '1', name: 'Ahmed Al-Rashid', company: 'Dubai Food Imports LLC', email: 'ahmed@dubaifood.ae', country: 'UAE', product: 'Frozen Green Peas', quantity: '5 MT', buyerType: 'Wholesaler', message: 'Please send us your best price for 5 MT green peas monthly.', createdAt: new Date().toISOString(), status: 'new' },
        { _id: '2', name: 'John Smith', company: 'Pacific Grocery Co.', email: 'john@pacificgrocery.ca', country: 'Canada', product: 'Flavored Foxnuts (Makhana)', quantity: '500 kg', buyerType: 'Retail Chain Distributor', message: 'We are looking for a reliable supplier of makhana for our health food section.', createdAt: new Date(Date.now() - 86400000).toISOString(), status: 'contacted' },
        { _id: '3', name: 'Maria Santos', company: 'SpiceTrade Australia', email: 'maria@spicetrade.au', country: 'Australia', product: 'Organic Spices (All)', quantity: '2 MT', buyerType: 'Importer / Trader', message: 'Interested in organic spice range. Please share COA and FSSAI certificates.', createdAt: new Date(Date.now() - 172800000).toISOString(), status: 'new' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`/api/inquiries/${id}`, { status });
    } catch {}
    setInquiries(prev => prev.map(i => i._id === id ? { ...i, status } : i));
    if (selected?._id === id) setSelected(prev => ({ ...prev, status }));
  };

  const filtered = filter === 'all' ? inquiries : inquiries.filter(i => i.status === filter);

  if (!auth) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <div className="login-logo">Seth Mr. Sanwariya<br />Agro Primes</div>
          <div className="login-sub">Admin Dashboard</div>
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && password === ADMIN_PASS && setAuth(true)}
            className="login-input"
          />
          <button
            className="login-btn"
            onClick={() => password === ADMIN_PASS ? setAuth(true) : alert('Incorrect password')}
          >
            Login →
          </button>
        </div>
      </div>
    );
  }

  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    contacted: inquiries.filter(i => i.status === 'contacted').length,
    closed: inquiries.filter(i => i.status === 'closed').length,
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <div className="admin-logo">Seth Mr. Sanwariya Agro Primes</div>
          <div className="admin-title-sub">Lead Management Dashboard</div>
        </div>
        <button className="admin-logout" onClick={() => setAuth(false)}>Logout</button>
      </div>

      <div className="admin-body">
        {/* STATS */}
        <div className="admin-stats">
          {[
            { label: 'Total Inquiries', val: stats.total, color: '#1B4332' },
            { label: 'New Leads', val: stats.new, color: '#C9A227' },
            { label: 'Contacted', val: stats.contacted, color: '#2D6A4F' },
            { label: 'Closed', val: stats.closed, color: '#718096' },
          ].map(s => (
            <div key={s.label} className="admin-stat-card" style={{ borderTop: `4px solid ${s.color}` }}>
              <div className="admin-stat-num" style={{ color: s.color }}>{s.val}</div>
              <div className="admin-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="admin-main">
          {/* LIST */}
          <div className="admin-list-panel">
            <div className="admin-filter-bar">
              {['all', 'new', 'contacted', 'closed'].map(f => (
                <button
                  key={f}
                  className={`admin-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === 'all' ? ` (${stats.total})` : f === 'new' ? ` (${stats.new})` : f === 'contacted' ? ` (${stats.contacted})` : ` (${stats.closed})`}
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading inquiries...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>No inquiries found.</div>
            ) : (
              <div className="inquiry-list">
                {filtered.map(inq => (
                  <div
                    key={inq._id}
                    className={`inquiry-item ${selected?._id === inq._id ? 'active' : ''}`}
                    onClick={() => setSelected(inq)}
                  >
                    <div className="inq-top">
                      <div className="inq-name">{inq.name}</div>
                      <span className={`inq-status ${inq.status}`}>{inq.status}</span>
                    </div>
                    <div className="inq-company">{inq.company || '—'} · {inq.country}</div>
                    <div className="inq-product">{inq.product}</div>
                    <div className="inq-date">{new Date(inq.createdAt).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DETAIL */}
          <div className="admin-detail-panel">
            {!selected ? (
              <div className="detail-placeholder">
                <div style={{ fontSize: '48px' }}>📋</div>
                <p>Select an inquiry to view details</p>
              </div>
            ) : (
              <div className="detail-content">
                <div className="detail-header">
                  <div>
                    <h2 className="detail-name">{selected.name}</h2>
                    <div className="detail-company">{selected.company} — {selected.country}</div>
                  </div>
                  <span className={`inq-status ${selected.status}`}>{selected.status}</span>
                </div>

                <div className="detail-grid">
                  {[
                    { label: 'Email', value: selected.email, href: `mailto:${selected.email}` },
                    { label: 'Phone', value: selected.phone || '—', href: selected.phone ? `tel:${selected.phone}` : null },
                    { label: 'Product', value: selected.product },
                    { label: 'Quantity', value: selected.quantity || '—' },
                    { label: 'Buyer Type', value: selected.buyerType || '—' },
                    { label: 'Date', value: new Date(selected.createdAt).toLocaleString() },
                  ].map(d => (
                    <div key={d.label} className="detail-field">
                      <div className="detail-field-label">{d.label}</div>
                      {d.href ? (
                        <a href={d.href} className="detail-field-link">{d.value}</a>
                      ) : (
                        <div className="detail-field-value">{d.value}</div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="detail-message">
                  <div className="detail-field-label">Message</div>
                  <p>{selected.message || '—'}</p>
                </div>

                <div className="detail-actions">
                  <div className="detail-actions-label">Update Status:</div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {['new', 'contacted', 'closed'].map(s => (
                      <button
                        key={s}
                        className={`status-btn ${selected.status === s ? 'active' : ''}`}
                        onClick={() => updateStatus(selected._id, s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <a href={`mailto:${selected.email}`} className="action-btn email-btn">
                      ✉ Send Email
                    </a>
                    {selected.phone && (
                      <a
                        href={`https://wa.me/${selected.phone.replace(/\D/g, '')}?text=Hello%20${encodeURIComponent(selected.name)}%2C%20Thank%20you%20for%20your%20inquiry%20about%20${encodeURIComponent(selected.product)}.`}
                        target="_blank" rel="noreferrer"
                        className="action-btn wa-btn"
                      >
                        💬 WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
